import { getMarsPrice, getMarsPricesBatch, type MarsPriceTier } from './mars-prices';
import { getKlimaMarsUnitCost, isKlimaInnerUnit } from './klima-pairing';

export interface ProfitCalculationItem {
  sku: string | null | undefined;
  productName?: string | null;
  saleDate: Date | string;
  quantity: number;
  revenue: number;
  cost_each?: number;
  current_cost?: number;
}

export interface ProfitCalculationResult {
  cost: number;
  profit: number;
  profitMargin: number;
}

/**
 * Calculate profit for a single item, using Mars prices if enabled
 */
export async function calculateItemProfit(
  item: ProfitCalculationItem,
  marsPriceTier: MarsPriceTier | null = null,
  useMarsPrices: boolean = false
): Promise<ProfitCalculationResult> {
  let cost = 0;

  if (useMarsPrices && item.sku && marsPriceTier) {
    const marsPrice = await getMarsPrice(item.sku, item.saleDate, marsPriceTier);
    cost = getKlimaMarsUnitCost(
      item.productName,
      marsPrice,
      item.cost_each || item.current_cost || 0
    );
  } else {
    // Use regular cost
    cost = item.cost_each || item.current_cost || 0;
  }

  const totalCost = cost * item.quantity;
  const profit = item.revenue - totalCost;
  const profitMargin = item.revenue > 0 ? (profit / item.revenue) * 100 : 0;

  return {
    cost: totalCost,
    profit,
    profitMargin,
  };
}

/**
 * Calculate profit for multiple items in batch (more efficient)
 * This fetches Mars prices in batch for better performance
 * @param sameMonthOnly If true, only use Mars prices from the same month as sale date
 */
export async function calculateItemsProfitBatch(
  items: ProfitCalculationItem[],
  marsPriceTier: MarsPriceTier | null = null,
  useMarsPrices: boolean = false,
  sameMonthOnly: boolean = false
): Promise<ProfitCalculationResult[]> {
  let marsPriceMap = new Map<string, number>();

  if (useMarsPrices && marsPriceTier) {
    // Fetch Mars prices in batch
    const itemsToFetch = items
      .filter(item => item.sku && !isKlimaInnerUnit(item.productName))
      .map(item => ({
        sku: item.sku!,
        saleDate: item.saleDate,
        tier: marsPriceTier,
      }));

    if (itemsToFetch.length > 0) {
      marsPriceMap = await getMarsPricesBatch(itemsToFetch, sameMonthOnly);
    }
  }

  // Calculate profit for each item
  return items.map((item) => {
    let cost = 0;

    if (useMarsPrices && item.sku && marsPriceTier) {
      const marsPrice = marsPriceMap.get(item.sku);
      cost = getKlimaMarsUnitCost(
        item.productName,
        marsPrice,
        item.cost_each || item.current_cost || 0
      );
    } else {
      // Use regular cost
      cost = item.cost_each || item.current_cost || 0;
    }

    const totalCost = cost * item.quantity;
    const profit = item.revenue - totalCost;
    const profitMargin = item.revenue > 0 ? (profit / item.revenue) * 100 : 0;

    return {
      cost: totalCost,
      profit,
      profitMargin,
    };
  });
}
